import { APIHierarchicalDashboardFilterSuggestion } from '@ardoq/api-types';
import { HierarchicalDashboardFilterSuggestion } from '../types';

export const toHierarchicalDashboardFilterSuggestions = (
  apiHierarchicalSuggestions: APIHierarchicalDashboardFilterSuggestion[]
): HierarchicalDashboardFilterSuggestion[] => {
  const optionsMap = new Map<string, HierarchicalDashboardFilterSuggestion>(
    apiHierarchicalSuggestions.map(suggestion => [
      suggestion.value,
      { ...suggestion, children: [] },
    ])
  );

  const rootsOptions: HierarchicalDashboardFilterSuggestion[] = [];
  for (const option of optionsMap.values()) {
    const parentOption =
      // prevent circular reference if parentValue is the same as value, which would cause an infinite loop in toHierarchicalDashboardFilterSuggestions
      option.parentValue && option.value !== option.parentValue
        ? optionsMap.get(option.parentValue)
        : undefined;

    if (parentOption) {
      parentOption.children.push(option);
    } else {
      rootsOptions.push(option);
    }
  }

  return rootsOptions;
};

export const MAX_HIERARCHICAL_OPTIONS = 100;

// --- Hierarchy list filtering ---
// Filter the hierarchy based on a search query.
// A node is included if it matches the query itself or if it has any descendant that matches the query,
// so a branch is included if any of its descendants match, preserving the hierarchy structure.
export const filterHierarchy = (
  nodes: HierarchicalDashboardFilterSuggestion[],
  query: string
): HierarchicalDashboardFilterSuggestion[] => {
  if (query.length === 0) return nodes;
  const lowerQuery = query.toLowerCase();

  const prune = (
    node: HierarchicalDashboardFilterSuggestion
  ): HierarchicalDashboardFilterSuggestion | null => {
    if (node.label.toLowerCase().includes(lowerQuery)) return node;

    const filteredChildren = node.children
      .map(prune)
      .filter((n): n is HierarchicalDashboardFilterSuggestion => n !== null);

    if (filteredChildren.length === 0) return null;

    return { ...node, children: filteredChildren };
  };

  return nodes
    .map(prune)
    .filter((n): n is HierarchicalDashboardFilterSuggestion => n !== null);
};

export const flattenHierarchy = (
  nodes: HierarchicalDashboardFilterSuggestion[]
): HierarchicalDashboardFilterSuggestion[] =>
  nodes.flatMap(node => [node, ...flattenHierarchy(node.children)]);

export const limitHierarchy = (
  nodes: HierarchicalDashboardFilterSuggestion[],
  max: number
): HierarchicalDashboardFilterSuggestion[] => {
  let remaining = max;
  const takeNodesUpToLimit = (
    suggestions: HierarchicalDashboardFilterSuggestion[]
  ): HierarchicalDashboardFilterSuggestion[] => {
    const result: HierarchicalDashboardFilterSuggestion[] = [];
    for (const node of suggestions) {
      if (remaining <= 0) break;
      if (node.isSelectable) remaining--;
      const children = takeNodesUpToLimit(node.children);
      if (!node.isSelectable && children.length === 0) continue;
      result.push({ ...node, children });
    }
    return result;
  };
  return takeNodesUpToLimit(nodes);
};

export const filterAndLimitHierarchy = (
  nodes: HierarchicalDashboardFilterSuggestion[],
  query: string
): HierarchicalDashboardFilterSuggestion[] =>
  limitHierarchy(filterHierarchy(nodes, query), MAX_HIERARCHICAL_OPTIONS);
